// LA ZONA DE SUBIDA de las altas — ADR-2026-09-24-las-altas-del-selector-de-fuentes-en-dos-columnas.
//
// El recuadro punteado que ocupa la columna de "lo que se hace" en las cuatro altas del
// selector de fuentes (producto, foto del avatar, pose, fondo). Antes cada una resolvía
// la entrada a su manera —un botón azul full-width en producto y avatar, un recuadro
// punteado en fondo, un botón al pie en pose—, así que la misma acción se veía distinta
// en cada pestaña y el botón azul gigante se comía el ancho de la ventana.
//
// Acepta ARRASTRAR además de clickear: en el escritorio es lo primero que se intenta con
// un recuadro punteado, y antes no hacía nada en ninguna de las cuatro. El input de
// archivo vive acá adentro para que el host no tenga que cablear uno por pantalla.
//
// No sabe subir nada: devuelve los `File` elegidos y el alta hace el resto.

import React, { useRef, useState } from 'react';
import { Upload, type LucideIcon } from 'lucide-react';

export interface ZonaDeSubidaProps {
  /** El renglón grande: qué se sube ("Subí la foto de tu producto"). */
  titulo: string;
  /** El renglón chico de abajo: formatos, cantidad, lo que aclare. */
  ayuda?: string;
  Icon?: LucideIcon;
  /** Varios archivos de una (producto). El resto sube de a uno. */
  multiple?: boolean;
  accept?: string;
  /** Apagada mientras se procesa lo anterior; `titulo` suele decir qué está pasando. */
  deshabilitada?: boolean;
  /**
   * `alta` estira el recuadro a todo el alto que le deje la columna (`flex-1`), que es
   * lo que evita el hueco blanco debajo cuando el aside de al lado es más largo.
   * `normal` lo deja del alto de su contenido.
   */
  alto?: 'normal' | 'alta';
  onArchivos: (archivos: File[]) => void;
}

export const ZonaDeSubida: React.FC<ZonaDeSubidaProps> = ({
  titulo,
  ayuda,
  Icon = Upload,
  multiple = false,
  accept = 'image/*',
  deshabilitada = false,
  alto = 'normal',
  onArchivos,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [encima, setEncima] = useState(false);

  // Pasa lo que el navegador dice que es una imagen Y TAMBIÉN lo que llega con el MIME
  // VACÍO (ADR-2026-09-24-la-zona-de-subida-no-se-come-lo-que-no-tiene-mime): un HEIC, un
  // AVIF o un WebP pueden venir con `type: ''` según el navegador y el origen del
  // archivo, y aguas abajo `normalizeImageForUpload` los reconoce por extensión y los
  // convierte. Filtrarlos acá los hacía desaparecer sin vista previa y sin mensaje — y lo
  // que de verdad no sirve (RAW, TIFF, GIF, SVG) también llega con MIME vacío y tiene que
  // pasar para que el alta lo rechace DICIENDO por qué. Lo que sí se corta es lo que se
  // declara otra cosa (un PDF, un video).
  const entregar = (lista: FileList | null) => {
    const archivos = Array.from(lista ?? []).filter(
      (f) => !f.type || f.type.startsWith('image/'),
    );
    if (archivos.length === 0) return;
    onArchivos(multiple ? archivos : archivos.slice(0, 1));
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        className="hidden"
        onChange={(e) => {
          entregar(e.target.files);
          // Para que elegir DOS VECES el mismo archivo vuelva a disparar el change.
          e.target.value = '';
        }}
      />
      <button
        type="button"
        disabled={deshabilitada}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => {
          if (deshabilitada) return;
          e.preventDefault();
          setEncima(true);
        }}
        onDragLeave={() => setEncima(false)}
        onDrop={(e) => {
          if (deshabilitada) return;
          e.preventDefault();
          setEncima(false);
          entregar(e.dataTransfer.files);
        }}
        /* `alta` sólo estira en escritorio: en el teléfono un recuadro que se come el
           alto de la hoja deja el ejemplo y los tips abajo del pliegue. */
        className={`w-full ${alto === 'alta' ? 'min-h-[150px] md:min-h-[180px] md:flex-1' : 'min-h-[150px]'} flex flex-col items-center justify-center gap-2.5 rounded-2xl border-2 border-dashed px-6 py-8 text-center transition-colors disabled:cursor-wait disabled:opacity-60 ${
          encima
            ? 'border-brand-primary bg-brand-primary/[0.06]'
            : 'border-neutral-300 bg-white hover:border-brand-primary/50 hover:bg-brand-primary/[0.03]'
        }`}
      >
        <span
          className={`inline-flex h-11 w-11 items-center justify-center rounded-full transition-colors ${
            encima ? 'bg-brand-primary text-white' : 'bg-brand-primary/10 text-brand-primary'
          }`}
        >
          <Icon className="h-5 w-5" />
        </span>
        <span className="text-[15px] font-semibold text-brand-black/85">{titulo}</span>
        {ayuda && (
          <span className="max-w-[34ch] text-[12.5px] leading-snug text-[#3C3C43]/55">
            {ayuda}
          </span>
        )}
      </button>
    </>
  );
};

export default ZonaDeSubida;
